import React, { useCallback, useContext, useEffect, useState } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { AuthProvider, AuthContext } from "./src/contexts/AuthContext";
import useAxiosInterceptor from "./src/hooks/useAxiosInterceptor";
import SignInPage from "./src/screens/Auth/SignInPages";
import { Provider as PaperProvider } from "react-native-paper";
import AppStackNavigator from "./src/navigation/AppStackNavigator";
import { UserProvider } from "./src/contexts/EmulateUserContext";
import Toast from "react-native-toast-message";
import { useFonts } from "expo-font";
import * as SplashScreen from "expo-splash-screen";
import * as Updates from "expo-updates";
import {
  View,
  StyleSheet,
  AppState,
  Platform,
  StatusBar,
  Modal,
  TouchableOpacity,
} from "react-native";
import AppText from "./src/components/AppText";
import BiometricAuthScreen from "./src/screens/Auth/Biometric/Biometric";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { RefreshProvider } from "./src/contexts/refreshContext";
import PersonalDetailsStyle from "./src/screens/Profile/PersonalDetails/PersonalDetailsStyle";
import { getUpdateStatus } from "./src/utils/forceUpdate";
import ForceUpdateScreen from "./src/components/ForceUpdateScreen";
import AppleCallbackPage from "./src/screens/Auth/AppleCallbackPage";
import AccountNotFound from "./src/screens/Auth/AccountNotFound";
import commonStyles from "./src/styles/commonStyles";
import {
  SafeAreaProvider,
  SafeAreaView,
} from "react-native-safe-area-context";

const linking = {
  prefixes: [
    "https://mobile-dot-dev-epicportal.uc.r.appspot.com",
    "https://mobile-dot-mobile-prod-epic.uc.r.appspot.com",
    "https://mobile-prod-epic.uc.r.appspot.com",
    "http://localhost:19006",
  ], // your domain and local dev
  config: {
    screens: {
      Home: "",
      Login: "login",
      AppleCallback: "apple-callback", // maps /apple-callback → AppleCallbackPage
    },
  },
};

const AuthStack = createStackNavigator();

const AuthStackScreen: React.FC = () => (
  <AuthStack.Navigator>
    <AuthStack.Screen
      name="SignIn"
      component={SignInPage}
      options={{ headerShown: false }}
    />
    <AuthStack.Screen
      name="AppleCallback"
      component={AppleCallbackPage}
      options={{ headerShown: false }}
    />
    <AuthStack.Screen
      name="AccountNotFound"
      component={AccountNotFound}
      options={{ headerShown: false }}
    />
  </AuthStack.Navigator>
);

const AppStackScreen: React.FC = () => {
  useAxiosInterceptor();
  return <AppStackNavigator />;
};

const AppNavigator: React.FC = () => {
  const { userToken } = useContext(AuthContext);
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  useEffect(() => {
    const checkLastExitTime = async () => {
      const saved = await AsyncStorage.getItem("backgroundTime");
      const elapsed = saved ? Date.now() - parseInt(saved, 10) : null;

      if (userToken) {
        if (elapsed && elapsed >= 5 * 60 * 1000) {
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } else {
        setIsAuthenticated(true);
      }

      await AsyncStorage.removeItem("backgroundTime");
    };

    checkLastExitTime();
  }, []);

  useEffect(() => {
    if (!userToken) return;

    let lastAppState = AppState.currentState;

    const handleAppStateChange = async (nextAppState: any) => {
      if (nextAppState === "background") {
        const now = Date.now();
        await AsyncStorage.setItem("backgroundTime", now.toString());
      } else if (lastAppState === "background" && nextAppState === "active") {
        const saved = await AsyncStorage.getItem("backgroundTime");
        if (saved) {
          const elapsed = Date.now() - parseInt(saved, 10);
          if (elapsed >= 5 * 60 * 1000) {
            setIsAuthenticated(false);
          }
        }
        await AsyncStorage.removeItem("backgroundTime");
      }

      lastAppState = nextAppState;
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [userToken]);

  useEffect(() => {
    if (userToken) {
      // setIsAuthenticated(true); // uncomment if biometric required only after timeout
    } else {
      setIsAuthenticated(true);
    }
  }, [userToken]);

  if (!isAuthenticated && Platform.OS !== "web") {
    return (
      <BiometricAuthScreen onAuthSuccess={() => setIsAuthenticated(true)} />
    );
  }

  return userToken ? <AppStackScreen /> : <AuthStackScreen />;
};

const App: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [appIsReady, setAppIsReady] = useState(false);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [updateUrl, setUpdateUrl] = useState<string | undefined>();
  const [fontsLoaded] = useFonts({
    BarisSans_Thin: require("./assets/fonts/BarisSans/BrisaSans-Thin.otf"),
    BarisSans_Light: require("./assets/fonts/BarisSans/BrisaSans-Light.otf"),
    BarisSans_Regular: require("./assets/fonts/BarisSans/BrisaSans-Regular.otf"),
    BarisSans_Medium: require("./assets/fonts/BarisSans/BrisaSans-Medium.otf"),
    BarisSans_Bold: require("./assets/fonts/BarisSans/BrisaSans-Bold.otf"),
  });

  // ✅ Check for OTA update in background
  useEffect(() => {
    const fetchAndApplyUpdates = async () => {
      try {
        const update = await Updates.checkForUpdateAsync();
        if (update.isAvailable) {
          await Updates.fetchUpdateAsync();
          setModalVisible(true);
        }
      } catch (e) {
        console.warn("❌ OTA update check failed:", e);
      }
    };

    fetchAndApplyUpdates();
  }, []);

  // Force-update gate: once the app is ready, check the store version and,
  // if an update is required, flag it so the blocking screen renders.
  useEffect(() => {
    if (!appIsReady) return;

    let isMounted = true;
    (async () => {
      const status = await getUpdateStatus();
      if (isMounted && status.needsUpdate) {
        setUpdateUrl(status.url);
        setUpdateRequired(true);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [appIsReady]);

  useEffect(() => {
    async function prepareApp() {
      try {
        await SplashScreen.preventAutoHideAsync();
        if (fontsLoaded) {
          setAppIsReady(true);
        }
      } catch (error) {
        console.warn(error);
      }
    }

    prepareApp();
  }, [fontsLoaded]);

  // Safeguard: never trap the user on the splash screen. If fonts fail to
  // load (or take too long), force the app to become ready after a timeout.
  useEffect(() => {
    if (appIsReady) return;
    const timeout = setTimeout(() => {
      console.warn("⚠️ Fonts not ready after timeout — forcing app ready.");
      setAppIsReady(true);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [appIsReady]);

  const onLayoutRootView = useCallback(async () => {
    if (appIsReady) {
      await SplashScreen.hideAsync();
    }
  }, [appIsReady]);

  if (!appIsReady) {
    return null;
  }

  // Hard gate: when an update is required, render only the blocking screen.
  // The app navigation tree never mounts, so the user cannot continue.
  if (updateRequired) {
    return (
      <SafeAreaProvider>
        <SafeAreaView style={[styles.container]} onLayout={onLayoutRootView}>
          <ForceUpdateScreen storeUrl={updateUrl} />
        </SafeAreaView>
      </SafeAreaProvider>
    );
  }

  const handleRestart = async () => {
    setModalVisible(false);
    await Updates.fetchUpdateAsync();

    await Updates.reloadAsync();
  };

  return (
    <>
      <SafeAreaProvider>
        <AuthProvider>
          <UserProvider>
            <RefreshProvider>
              <NavigationContainer linking={linking}>
                <View style={styles.container} onLayout={onLayoutRootView}>
                  <StatusBar
                    translucent
                    backgroundColor="transparent"
                    barStyle="dark-content"
                  />
                  <PaperProvider>
                    <AppNavigator />
                  </PaperProvider>
                </View>
              </NavigationContainer>
            </RefreshProvider>
          </UserProvider>
          <Toast />
        </AuthProvider>
      </SafeAreaProvider>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => {}}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <AppText style={styles.title}>Update Downloaded</AppText>
            <AppText style={styles.message}>
              A new version of the app has been downloaded. Press "OK" to
              restart and apply the update.
            </AppText>

            <TouchableOpacity style={styles.button} onPress={handleRestart}>
              <AppText
                style={[
                  PersonalDetailsStyle.buttonText,
                  Platform.OS === "android" && { marginTop: 4 },
                ]}
              >
                OK
              </AppText>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.5)",

    alignItems: "center",
  },
  modalContent: {
    margin: 20,
    backgroundColor: "white",
    borderRadius: 8,
    padding: 24,
    alignItems: "center",
    width: "70%",
  },
  title: {
    fontWeight: "bold",
    fontSize: 18,
    marginBottom: 10,
  },
  message: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: "center",
  },
  button: {
    backgroundColor: "#3629B7",
    borderRadius: 10,
    padding: 7,
    alignItems: "center",
    width: "30%",
  },
});

export default App;
